// Dependencies
import React, { useMemo, useState, useEffect } from 'react';

import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  flexRender,
} from '@tanstack/react-table';

// Types
import { BudgetItemsType, TableProps, columnNames } from './types';

// Styles
import {
  Container,
  TableContainer,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Tfoot,
  TfootTd,
} from '../SummaryTablesStyles';

// Components
import { Typography } from '../../../Typography';
import { TruncatedText } from '../../../TruncatedText';

// Utils
import { formatQuantity } from '../../../../../utils/decimalFormat';

// Styled Components for category indicator
import styled from 'styled-components';

const CategoryCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CategoryIndicator = styled.div<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${(props) => props.color || '#cccccc'};
  flex-shrink: 0;
`;

// Format currency for display
const formatCurrency = (value?: number): string => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('ca-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// Hook para calcular maxLength responsive
const useResponsiveMaxLength = () => {
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calcular maxLength basado en el ancho de pantalla
  const getMaxLength = (baseLength: number, columnWidthPercentage: number) => {
    // Ancho estimado disponible para la columna
    const availableWidth = screenWidth * (columnWidthPercentage / 100);

    // Aproximadamente 8px por carácter en tamaño S
    const charactersPerPixel = 0.125;
    const calculatedLength = Math.floor(availableWidth * charactersPerPixel);

    // Usar el menor entre el cálculo y el baseLength, con un mínimo de 10
    return Math.max(10, Math.min(baseLength, calculatedLength));
  };

  return { getMaxLength, screenWidth };
};

export const BudgetItemsTable = ({
  data,
  ...props
}: TableProps): JSX.Element => {
  const dataAll = data || [];
  const { getMaxLength } = useResponsiveMaxLength();

  // Check if any row has price data
  const hasPriceData = useMemo(() => {
    return dataAll.some(
      (item) =>
        item.unitPrice !== undefined &&
        item.unitPrice !== null &&
        item.totalAmount !== undefined &&
        item.totalAmount !== null
    );
  }, [dataAll]);

  // Calculate total amount
  const totalAmount = useMemo(() => {
    if (!hasPriceData) return 0;
    return dataAll.reduce((sum, item) => {
      const amount =
        typeof item.totalAmount === 'number' ? item.totalAmount : 0;
      return sum + amount;
    }, 0);
  }, [dataAll, hasPriceData]);

  const columnHelper = createColumnHelper<BudgetItemsType>();

  const baseColumns = useMemo(
    () => [
      columnHelper.accessor((row) => row.codiPartida, {
        id: columnNames.codiPartida.id,
        header: () => columnNames.codiPartida.label,
      }),
      columnHelper.accessor((row) => row.partida, {
        id: columnNames.partida.id,
        header: () => columnNames.partida.label,
        cell: (info) => (
          <TruncatedText
            text={info.getValue()}
            maxLength={getMaxLength(60, hasPriceData ? 25 : 35)} // Ajustar ancho si hay columnas de precio
            size="S"
            variant="regular"
          />
        ),
      }),
      columnHelper.accessor((row) => row.quantity, {
        id: columnNames.quantity.id,
        header: () => columnNames.quantity.label,
        cell: (info) =>
          formatQuantity(info.getValue() as number | string),
      }),
      columnHelper.accessor((row) => row.categoria, {
        id: columnNames.categoria.id,
        header: () => columnNames.categoria.label,
        cell: (info) => {
          const row = info.row.original;
          return (
            <CategoryCell>
              <CategoryIndicator color={row.categoryColor || '#cccccc'} />
              <TruncatedText
                text={info.getValue()}
                maxLength={getMaxLength(40, hasPriceData ? 15 : 25)} // Ajustar ancho si hay columnas de precio
                size="S"
                variant="regular"
              />
            </CategoryCell>
          );
        },
      }),
    ],
    [getMaxLength, hasPriceData]
  );

  // Add price columns only if data has price information
  const priceColumns = useMemo(() => {
    if (!hasPriceData) return [];
    return [
      columnHelper.accessor((row) => row.unitPrice, {
        id: columnNames.unitPrice.id,
        header: () => columnNames.unitPrice.label,
        cell: (info) => formatCurrency(info.getValue()),
      }),
      columnHelper.accessor((row) => row.totalAmount, {
        id: columnNames.totalAmount.id,
        header: () => columnNames.totalAmount.label,
        cell: (info) => formatCurrency(info.getValue()),
      }),
    ];
  }, [hasPriceData]);

  const columns = useMemo(
    () => [...baseColumns, ...priceColumns],
    [baseColumns, priceColumns]
  );

  const table = useReactTable({
    data: dataAll,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Count columns for the total row colspan
  const totalColumnsBeforeAmount = hasPriceData
    ? columns.length - 1
    : columns.length;

  return (
    <Container>
      <TableContainer>
        <Table {...props}>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
          {hasPriceData && totalAmount > 0 && (
            <Tfoot>
              <Tr>
                <TfootTd colSpan={totalColumnsBeforeAmount}>
                  <Typography size="S" variant="semiBold">
                    TOTAL
                  </Typography>
                </TfootTd>
                <TfootTd>
                  <Typography size="S" variant="semiBold">
                    {formatCurrency(totalAmount)}
                  </Typography>
                </TfootTd>
              </Tr>
            </Tfoot>
          )}
        </Table>
      </TableContainer>
    </Container>
  );
};
